import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { ResizeEvent } from "angular-resizable-element";
import { ToastrService } from "ngx-toastr";
import { Event } from "../../../../models/event";
import { EventType } from "../../../../models/event-type";
import { Registration } from "../../../../models/registration";
import { CostService } from "../../../../services/cost-controller/cost.service";
import { InstitutionService } from "../../../../services/institution-controller/institution.service";
import { RegistrationControllerService } from "../../../../services/registration-controller/registration-controller.service";
import { RegistrationService } from "../../../../services/registration-controller/registration.service";
import { RoleInstitutionControllerService } from "../../../../services/role-controller/role-institution-controller.service";
import { Store } from "../../../../services/store.service";
import { TranslatedBaseError } from "../../TranslatedBaseError";
import { TranslateService } from "@ngx-translate/core";
import {
  DeterminantControllerService,
} from "../../../../services/registration-controller/determinant-controller.service";
import { RegistrationInstitution } from "../../../../models/registration-institution";

import { RecoverOrphanConfig } from "../../../../models/recover-orphan-config";
import { ServiceControllerService } from "../../../../services/service-controller/service-controller.service";

@Component({
  selector: "app-registration-detail",
  templateUrl: "./registration-detail.component.html",
  styleUrls: ["./registration-detail.component.css"],
  providers: [RegistrationControllerService, RoleInstitutionControllerService],
})
export class RegistrationDetailComponent extends TranslatedBaseError implements OnInit {


  public registration: Registration = new Registration();
  public serviceId: string;
  public registrationId: string;
  public subscription: any;
  private subscriptions: Subscription[] = [];
  public registrationInstitutionsLength: number = 0;
  public registrationUnitsLength: number = 0;
  public registrationInstitution: RegistrationInstitution = new RegistrationInstitution();

  public determinantMapById: { [key: string]: string } = {};

  public editMode: boolean = false;
  public initialValue: string = "";
  public isLoading: boolean = false;
  public disabled: string = "disabled";
  public selectedByDefault: string = "selectedByDefault";

  public regDeterminant: any;

  public debugProps: { bugSource: string; bugType: string, debugMode: boolean } = { bugSource: null, bugType: null, debugMode: false };
  public debugErrors: RecoverOrphanConfig[] = [];

  // for Physical results
  public havePhysicalResults: boolean = false;

  public style: object = {};

  public size = {
    width: 100,
    height: 100,
  };

  constructor(private registrationController: RegistrationControllerService,
    private registrationService: RegistrationService,
    private route: ActivatedRoute,
    private costService: CostService,
    private institutionService: InstitutionService,
    private determinantService: DeterminantControllerService,
    public store: Store,
    private router: Router,
    private serviceControllerService: ServiceControllerService,
    toastr: ToastrService,
    public translateService: TranslateService,
    private cdr: ChangeDetectorRef) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.reloadRegistration();
    this.store.state.common.sections.reset();
    this.checkDebugMode();
  }

  public checkDebugMode() {
    this.subscriptions.push(
      this.store.debugMode$.subscribe((mode) => {
        setTimeout(() => {
          console.log("Debug mode changed to: " + mode);
          this.debugProps.debugMode = mode;
          if (this.debugProps.debugMode) {
            this.fetchDebugErrors();
          } else {
            this.debugErrors = [];
          }
          this.cdr.detectChanges();
        });
      })
    );

    this.subscriptions.push(
      this.route.queryParamMap.subscribe((queryParams) => {
        setTimeout(() => {
          this.debugProps.bugSource = queryParams.get("bugsource");
          this.debugProps.bugType = queryParams.get("bugtype");
          this.cdr.detectChanges();
        });
      })
    );
  }

  private fetchDebugErrors() {
    if (!this.debugProps.debugMode || !this.serviceId) {
      return;
    }
    const cachedResults = this.store.state.common.dryRunResults.get(this.serviceId);
    if (cachedResults) {
      this.debugErrors = cachedResults;
      console.log("Debug errors fetched from cache: " + this.debugErrors);
    } else {
      this.serviceControllerService.recoverOrphanConfig(this.serviceId).subscribe((errors) => {
        this.store.state.common.dryRunResults.set(this.serviceId, errors);
        this.debugErrors = errors;
      });
    }
  }

  public ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public reloadRegistration() {
    this.route.params.subscribe((params) => {
      this.serviceId = params.serviceId;
      this.registrationId = params.registrationId;
      this.registrationController.getRegistration(this.registrationId).subscribe(
        (data) => {
          this.registration = data;
          this.fetchDebugErrors();
        },
      );
      this.getServiceDeterminantKeyName();
    });
  }

  public getServiceDeterminantKeyName() {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => Object.assign(this.determinantMapById, data),
    );
  }

  public saveTutorial(e) {
    if (e) {
      this.registration.tutorials.general = e;
      this.updateRegistration(this.registration);
    }
  }

  public refreshRegistration(event: Event) {
    if (event.eventType === EventType.Add || event.eventType === EventType.Update) {
      this.router.navigate(["/services/" + this.serviceId + "/registrations/" + event.id],
        { relativeTo: this.route, skipLocationChange: false });
    } else if (event.eventType === EventType.Delete) {
      this.router.navigate(["/services/" + this.serviceId + "/registrations"]);
    }
  }

  public reloadCosts() {
    this.costService.update.emit({ update: true });
  }

  public reloadInstitution() {
    this.institutionService.update.emit({ update: true });
  }

  public updateRegistrationInstitutionsLength(event) {
    this.registrationInstitutionsLength = event.registrationInstitutionsLength;
  }

  public updateRegistrationUnitsLength(event) {
    this.registrationUnitsLength = event.registrationUnitsLength;
  }

  public toggleEditMode() {
    this.editMode = !this.editMode;
  }
  public cancelEditMode() {
    this.editMode = false;
  }

  public submitEdit(event) {
    const ENTER = 13;
    if (event.keyCode === ENTER) {
      if (this.editMode) {
        this.isLoading = true;
        this.updateRegistration(this.registration);
        this.toggleEditMode();
      }
    }
  }

  public startEdit(event) {
    this.initialValue = event.target.value;
  }

  public submitEditOnFocus(event) {
    const value = event.target.value;
    if (value && value.length >= 2) {
      // Check if value has changed
      if (event.target.value !== this.initialValue) {
        this.initialValue = "";
        this.isLoading = true;
        this.updateRegistration(this.registration);
        this.toggleEditMode();
      }
    }
  }

  public updateRegistration(registration: Registration) {
    this.registrationController.saveRegistration(this.serviceId, registration).subscribe(
      (data) => {
        this.isLoading = false;
        this.responseSuccessful("Registration saved!");
        this.registrationService.changeMade();
      },
      (error) => {
        this.reloadRegistration();
        return this.showErrorMessage(error);
      },
    );
  }

  public toggleOperational() {
    this.isLoading = true;
    this.registration.active = !this.registration.active;
    this.registrationController.saveRegistration(this.serviceId, this.registration).subscribe(
      (data) => {
        this.isLoading = false;
        if (this.registration.active) {
          this.responseSuccessful("Registration activated!");
        } else {
          this.responseSuccessful("Registration inactivated!");
        }
      },
      (error) => {
        this.reloadRegistration();
        return this.showErrorMessage(error);
      },
    );
  }

  public onResizeEnd(event: ResizeEvent) {
    this.style = {
      width: `${event.rectangle.width}px`,
    };
  }

  public showIconFor() {
    if (this.isLoading) {
      return "fa-spinner fa-spin";
    }
    if (this.registration.active) {
      return "fa-toggle-large-on";
    }
    return "fa-toggle-large-on fa-flip-horizontal";
  }

  public closeSection() {
    this.store.state.common.sections.reset();
    this.store.state.common.determinantSection.reset();
    this.regDeterminant = null;
    this.style = {};
  }

  public onResultsChanged(event: boolean): void {
    this.havePhysicalResults = event;
  }

  public booleanValueChanged(event, type: string) {
    if (!this.isFieldDisabled(this.registration.ownership)) {
      if (type === "selectedByDefault") {
        this.registration.mandatorySelectedDefault = !this.registration.mandatorySelectedDefault;
        if (!this.registration.mandatorySelectedDefault) {
          this.registration.mandatoryChangeDisabled = false;
        }
      } else {
        this.registration.mandatoryChangeDisabled = !this.registration.mandatoryChangeDisabled;
        if (this.registration.mandatoryChangeDisabled && !this.registration.mandatorySelectedDefault) {
          this.registration.mandatorySelectedDefault = this.registration.mandatoryChangeDisabled;
        }
      }
      this.saveRegistration();
    }
  }

  public saveRegistrationDeterminant(event: any) {
    console.log("saveRegistrationDeterminant", event);
    if (event?.type && event?.jsonString) {
      this.registration[event.type] = event.jsonString;
      this.saveRegistration();
    }
  }

  public saveRegistration() {
    if (!this.isFieldDisabled(this.registration.ownership)) {
      this.registrationController.saveRegistration(this.serviceId, this.registration).subscribe(
        (data) => {
          this.responseSuccessful("Registration saved!");
        },
        (error) => {
          this.reloadRegistration();
          return this.showErrorMessage(error);
        },
      );
    }
  }

  public openDeterminantBuilderModal($event) {
    this.regDeterminant = $event;
  }

}
